/**
 * Agency Portal v2 §4.1 — Overview section.
 *
 * One `GET /v1/agency/overview` call fills every tile, the Next-steps
 * card. Recent-activity now reads a paginated endpoint
 * (`GET /v1/agency/activity`) with cursor-based "Load more". Scans
 * group by import to collapse the one-line-per-domain noise the
 * founder saw.
 */

import { useCallback, useEffect, useState } from 'react';

import { useOptionalHttp } from '../../api/http-context.js';
import {
  getAgencyActivity,
  getAgencyOverview,
  type AgencyActivityEntry,
  type AgencyOverviewWire,
} from '../../api/agency-overview.js';
import { useC } from '../../lib/theme.js';
import { Button, Card } from '../../components/atoms/index.js';
import type { PortalSection } from './portal-sections.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ok'; state: AgencyOverviewWire }
  | { kind: 'error'; message: string };

type ActivityState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok'; entries: readonly AgencyActivityEntry[]; cursor: string | null }
  | { kind: 'error'; message: string };

export interface PortalOverviewProps {
  readonly goTo: (section: PortalSection) => void;
}

export function PortalOverview({ goTo }: PortalOverviewProps): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [activity, setActivity] = useState<ActivityState>({ kind: 'idle' });
  const [loading_more, setLoadingMore] = useState(false);

  useEffect(() => {
    if (http === null) return;
    let cancelled = false;
    void (async () => {
      const [r, act] = await Promise.all([
        getAgencyOverview(http),
        getAgencyActivity(http, { limit: 10 }),
      ]);
      if (cancelled) return;
      if (r.kind === 'ok') setState({ kind: 'ok', state: r.state });
      else if (r.kind === 'forbidden') setState({ kind: 'error', message: 'Agency features are not enabled on this account.' });
      else setState({ kind: 'error', message: r.message });
      if (act.kind === 'ok') {
        setActivity({ kind: 'ok', entries: act.page.activity, cursor: act.page.next_cursor });
      } else {
        setActivity({ kind: 'error', message: act.kind === 'forbidden' ? 'Agency features are not enabled on this account.' : act.message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [http]);

  const onLoadMore = useCallback(async (): Promise<void> => {
    if (http === null) return;
    if (activity.kind !== 'ok' || activity.cursor === null) return;
    setLoadingMore(true);
    const r = await getAgencyActivity(http, { limit: 10, cursor: activity.cursor });
    setLoadingMore(false);
    if (r.kind === 'ok') {
      setActivity({
        kind: 'ok',
        entries: [...activity.entries, ...r.page.activity],
        cursor: r.page.next_cursor,
      });
    }
  }, [http, activity]);

  if (state.kind === 'loading') {
    return (
      <Card className="p-5" data-testid="portal-overview-loading">
        <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>Loading…</div>
      </Card>
    );
  }
  if (state.kind === 'error') {
    return (
      <Card className="p-5">
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>{state.message}</div>
      </Card>
    );
  }

  const s = state.state;
  // v3 (2026-10-10) — Widget leads tile: when the widget is not set
  // up, show "—" instead of a misleading "0". Context goes to the
  // subline. Reads clearly for a non-technical agency owner.
  const widgetState = s.widget.state;
  const widgetValue =
    widgetState === 'live' ? String(s.widget.leads_30d) : '—';
  const widgetSubline =
    widgetState === 'live'
      ? 'last 30 days'
      : widgetState === 'needs_origin'
        ? 'Needs a website'
        : 'Not set up';
  // Referral conversion only kicks in once there are real clicks.
  const referralSignupsLine =
    s.referral.signups_total === 1
      ? '1 signup to date'
      : `${s.referral.signups_total.toLocaleString('en-US')} signups to date`;

  const nextStepsCount = Object.values(s.next_steps).filter((v) => v).length;
  const nextStepsTotal = Object.keys(s.next_steps).length;
  const allDone = nextStepsCount === nextStepsTotal;

  // Order Next steps so the first incomplete one shows up as primary.
  const stepsOrder: Array<{ key: keyof AgencyOverviewWire['next_steps']; label: string; target: PortalSection; ctaLabel: string }> = [
    { key: 'first_client', label: 'Add your first client', target: 'clients', ctaLabel: 'Add client' },
    { key: 'first_scan', label: 'Scan a list of prospects', target: 'reports', ctaLabel: 'Import domains' },
    { key: 'widget_setup', label: 'Set up your scan widget', target: 'widget', ctaLabel: 'Set up widget' },
    { key: 'white_label_logo', label: 'Brand your reports', target: 'white-label', ctaLabel: 'Open White label' },
    { key: 'referral_slug_set', label: 'Share your referral link', target: 'referral', ctaLabel: 'Copy link' },
  ];
  const firstIncompleteIdx = stepsOrder.findIndex((step) => !s.next_steps[step.key]);

  return (
    <div className="space-y-6" data-testid="portal-overview">
      {/* Stat tiles */}
      <div
        className="grid gap-3"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        }}
      >
        <Stat
          C={C}
          label="Clients"
          value={String(s.clients_linked)}
          detail={s.clients_linked === 1 ? '1 linked account' : `${s.clients_linked} linked accounts`}
          onClick={() => goTo('clients')}
        />
        <Stat
          C={C}
          label="Reports scanned"
          value={String(s.reports_scanned_30d)}
          detail={`last 30 days · ${s.reports_sent_30d} sent by email`}
          onClick={() => goTo('reports')}
        />
        <Stat
          C={C}
          label="Referral clicks"
          value={String(s.referral.clicks_30d)}
          detail={`last 30 days · ${referralSignupsLine}`}
          onClick={() => goTo('referral')}
        />
        <Stat
          C={C}
          label="Widget leads"
          value={widgetValue}
          detail={widgetSubline}
          onClick={() => goTo(widgetState === 'live' ? 'leads' : 'widget')}
        />
      </div>

      {/* Next steps */}
      {!allDone ? (
        <Card className="p-5" data-testid="portal-overview-next-steps">
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              color: C.muted,
              letterSpacing: 2,
              textTransform: 'uppercase',
              marginBottom: 12,
            }}
          >
            Next steps
          </div>
          <div>
            {stepsOrder.map((step, i) => {
              const done = s.next_steps[step.key];
              const isPrimary = i === firstIncompleteIdx;
              return (
                <div
                  key={step.key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 10,
                    padding: '8px 0',
                    borderTop: i === 0 ? 'none' : `1px solid ${C.borderSoft}`,
                  }}
                  data-testid="portal-overview-next-step"
                  data-done={done ? 'true' : 'false'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span
                      aria-hidden
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 16,
                        height: 16,
                        borderRadius: 999,
                        background: done ? C.lime : 'transparent',
                        border: `1px solid ${done ? C.lime : C.border}`,
                        color: C.ink,
                        fontFamily: monoFont,
                        fontSize: 10,
                      }}
                    >
                      {done ? '✓' : ''}
                    </span>
                    <span
                      style={{
                        fontFamily: sansFont,
                        fontSize: 13,
                        color: done ? C.muted : C.text,
                        textDecoration: done ? 'line-through' : 'none',
                      }}
                    >
                      {step.label}
                    </span>
                  </div>
                  {!done ? (
                    <Button
                      variant={isPrimary ? 'primary' : 'secondary'}
                      size="sm"
                      onClick={() => goTo(step.target)}
                    >
                      {step.ctaLabel}
                    </Button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </Card>
      ) : null}

      {/* Recent activity — v4 (2026-10-10) grouped + paginated. */}
      <Card className="p-5" data-testid="portal-overview-activity">
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 9,
            color: C.muted,
            letterSpacing: 2,
            textTransform: 'uppercase',
            marginBottom: 12,
          }}
        >
          Recent activity
        </div>
        {activity.kind === 'ok' && activity.entries.length === 0 ? (
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
            Nothing yet. Scan a prospect or share your referral link to get started.
          </div>
        ) : activity.kind === 'error' ? (
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
            {activity.message}
          </div>
        ) : activity.kind === 'loading' || activity.kind === 'idle' ? (
          <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>Loading…</div>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {activity.entries.map((a, i) => {
              const content = (
                <>
                  <span data-testid="portal-overview-activity-summary">{a.summary}</span>
                  <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>
                    {formatAt(a.at)}
                  </span>
                </>
              );
              const wrapperStyle: React.CSSProperties = {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 10,
                padding: '8px 0',
                borderTop: i === 0 ? 'none' : `1px solid ${C.borderSoft}`,
                fontFamily: sansFont,
                fontSize: 13,
                color: C.text,
              };
              return (
                <li
                  key={`${a.at}-${i}`}
                  data-testid="portal-overview-activity-item"
                  data-kind={a.kind}
                >
                  {a.href !== undefined ? (
                    <a
                      href={a.href}
                      style={{
                        ...wrapperStyle,
                        textDecoration: 'none',
                        color: C.text,
                        cursor: 'pointer',
                      }}
                    >
                      {content}
                    </a>
                  ) : (
                    <div style={wrapperStyle}>{content}</div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {activity.kind === 'ok' && activity.cursor !== null ? (
          <div style={{ marginTop: 12 }}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void onLoadMore()}
              loading={loading_more}
              data-testid="portal-overview-activity-load-more"
            >
              Load more
            </Button>
          </div>
        ) : null}
      </Card>
    </div>
  );
}

function Stat({
  C,
  label,
  value,
  detail,
  onClick,
}: {
  C: ReturnType<typeof useC>;
  /** v3 (2026-10-10) — sentence-case label; the Overview is for a
   *  non-technical agency owner, not an engineer reading mono kickers. */
  label: string;
  value: string;
  detail: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="portal-overview-stat"
      style={{
        display: 'block',
        textAlign: 'left',
        padding: 16,
        border: `1px solid ${C.border}`,
        background: C.surface,
        borderRadius: 2,
        cursor: 'pointer',
        width: '100%',
      }}
    >
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 12,
          color: C.muted,
          fontWeight: 500,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: serifFont,
          fontSize: 36,
          lineHeight: 1,
          color: C.text,
          marginTop: 6,
          letterSpacing: -0.5,
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 12,
          color: C.muted,
          marginTop: 6,
          lineHeight: 1.4,
        }}
      >
        {detail}
      </div>
    </button>
  );
}

function formatAt(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}
